import "./index.css";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { clsx } from "clsx";
import { Drawer } from "../Drawer";
import { ModalContent } from "./ModalContent";
import { ModalContext } from "./context";
import type { IModalProps } from "./types";

export const Modal = (props: IModalProps) => {
  const { open, onOpenChange } = props;
  const isMobile = useMediaQuery("(max-width: 639px)");

  // All hooks must be called before any conditional returns
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const { context: floatingContext } = useFloating({
    open,
    onOpenChange,
  });

  const dismiss = useDismiss(floatingContext, {
    enabled: !props.preventClose,
    escapeKey: !props.preventClose,
    outsidePress: props.preventClose ? false : props.closeOnOutsidePress,
    outsidePressEvent: 'mousedown',
  });

  const { getFloatingProps } = useInteractions([dismiss]);

  const handleDismiss = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAnimationEnd = useCallback(() => {
    if (!open) {
      setMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const timer = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [open]);

  // Conditional return after all hooks
  if (isMobile) {
    return <Drawer {...props} side="bottom" />;
  }

  const contextValue = {
    ...props,
    handleDismiss,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    mounted,
    visible,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      <FloatingPortal>
        {mounted && (
          <>
            <FloatingOverlay
              className={clsx(
                "nuco-modal__overlay",
                visible
                  ? "nuco-modal__overlay--visible"
                  : "nuco-modal__overlay--hidden",
              )}
              lockScroll
            />
            <FloatingFocusManager
              context={floatingContext}
              returnFocus={true}
              initialFocus={0}
              modal={true}
            >
              <ModalContent />
            </FloatingFocusManager>
          </>
        )}
      </FloatingPortal>
    </ModalContext.Provider>
  );
};
